import React, { useMemo } from 'react';
import {
    Box,
    List,
    ListItem,
    ListItemText,
    Typography,
    Chip,
    Tooltip,
    Avatar,
    Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Home as HomeIcon,
    Inventory as ProductIcon,
    ShoppingCart as OrderIcon,
    Warehouse as WarehouseIcon,
    LocalShipping as ShippingIcon,
    People as CustomersIcon,
    Assessment as AnalyticsIcon,
    Settings as SettingsIcon,
    AdminPanelSettings
} from '@mui/icons-material';

const SidebarWMS = ({ collapsed = false }) => {
    const { _t } = useTranslation('manager');
    const navigate = useNavigate();
    const location = useLocation();

    const basePath = location.pathname.startsWith('/manager') ? '/manager' : '/admin';

    const isActiveLink = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

    const detectActiveModule = () => {
        const path = location.pathname;
        if (path.includes('/orders') || path.includes('/picking') || path.includes('/marketplace')) return 'oms';
        if (path.includes('/logistics') || path.includes('/shipping') || path.includes('/transport') || path.includes('/tms')) return 'tms';
        if (path.includes('/ttms') || path.includes('/financial') || path.includes('/billing')) return 'ttms';
        if (path.includes('/customers') || path.includes('/workforce') || path.includes('/users') || path.includes('/crm')) return 'crm';
        return 'wms';
    };

    const activeModule = detectActiveModule();

    const moduleMenus = useMemo(() => ({
        wms: {
            title: 'Warehouse',
            color: '#4caf50',
            items: [
                { id: 'home', label: 'Home', path: `${basePath}/dashboard` },
                { id: 'products', label: 'Products', path: `${basePath}/products` },
                { id: 'inventory', label: 'Inventory', path: `${basePath}/inventory` },
                { id: 'warehouses', label: 'Warehouses', path: `${basePath}/warehouses` },
                { id: 'warehouse-3d', label: '3D View', path: `${basePath}/warehouse-3d` },
                { id: 'warehouse-layout-editor', label: 'Layout Editor', path: `${basePath}/warehouse-layout-editor` },
                { id: 'warehouses-list', label: 'Warehouses List', path: `${basePath}/warehouses-list` },
                { id: 'quality-control', label: 'Quality Control', path: `${basePath}/quality-control` },
                { id: 'cycle-count', label: 'Cycle Count', path: `${basePath}/cycle-count-management` },
                { id: 'putaway-rules', label: 'Putaway Rules', path: `${basePath}/putaway-rules` },
                { id: 'stock-reservations', label: 'Stock Reservations', path: `${basePath}/stock-reservations` },
                { id: 'automatic-reorder', label: 'Auto Reorder', path: `${basePath}/automatic-reorder` },
                { id: 'non-conformance', label: 'Non-Conformance', path: `${basePath}/non-conformance` },
                { id: 'stock-ageing', label: 'Stock Ageing', path: `${basePath}/stock-ageing` },
                { id: 'inventory-operations', label: 'Inventory Ops', path: `${basePath}/inventory-operations` },
                { id: 'warehouse-3d-tools', label: '3D Tools', path: `${basePath}/warehouse-3d-tools` },
                { id: 'analytics', label: 'Analytics', path: `${basePath}/analytics` },
                { id: 'settings', label: 'Settings', path: `${basePath}/settings` }
            ]
        },
        oms: {
            title: 'Orders',
            color: '#2196f3',
            items: [
                { id: 'home', label: 'Home', path: `${basePath}/dashboard` },
                { id: 'orders', label: 'Orders', path: `${basePath}/orders` },
                { id: 'picking-waves', label: 'Picking Waves', path: `${basePath}/picking-waves` },
                { id: 'picking-lists', label: 'Picking Lists', path: `${basePath}/picking-lists` },
                { id: 'picking-routes', label: 'Picking Routes', path: `${basePath}/picking-routes` },
                { id: 'returns', label: 'Returns', path: `${basePath}/returns` },
                { id: 'backorder', label: 'Backorders', path: `${basePath}/backorder-queue` },
                { id: 'order-operations', label: 'Order Ops', path: `${basePath}/order-operations` },
                { id: 'marketplace-operations', label: 'Marketplace', path: `${basePath}/marketplace-operations` },
                { id: 'product-operations', label: 'Product Ops', path: `${basePath}/product-operations` },
                { id: 'analytics', label: 'Analytics', path: `${basePath}/analytics` },
                { id: 'settings', label: 'Settings', path: `${basePath}/settings` }
            ]
        },
        tms: {
            title: 'Transport',
            color: '#e91e63',
            items: [
                { id: 'home', label: 'Home', path: `${basePath}/dashboard` },
                { id: 'logistics', label: 'Logistics', path: `${basePath}/logistics` },
                { id: 'shipping', label: 'Shipping', path: `${basePath}/shipping` },
                { id: 'transport', label: 'Transport', path: `${basePath}/transport-management` },
                { id: 'fleet', label: 'Fleet Management', path: `${basePath}/tms/fleet` },
                { id: 'dispatch', label: 'Driver Dispatch', path: `${basePath}/tms/dispatch` },
                { id: 'yard', label: 'Yard Management', path: `${basePath}/tms/yard` },
                { id: 'dock', label: 'Dock Scheduling', path: `${basePath}/tms/dock` },
                { id: 'claims', label: 'Claims', path: `${basePath}/tms/claims` },
                { id: 'billing', label: 'Freight Billing', path: `${basePath}/tms/billing` },
                { id: 'load-planning', label: 'Load Planning', path: `${basePath}/tms/load-planning` },
                { id: 'delivery-slots', label: 'Delivery Slots', path: `${basePath}/delivery-slot-management` },
                { id: 'partners', label: 'Partners', path: `${basePath}/tms/partners` },
                { id: 'tms-operations', label: 'TMS Ops', path: `${basePath}/tms-operations` },
                { id: 'analytics', label: 'Analytics', path: `${basePath}/analytics` },
                { id: 'settings', label: 'Settings', path: `${basePath}/settings` }
            ]
        },
        ttms: {
            title: 'Trading',
            color: '#ff5722',
            items: [
                { id: 'home', label: 'Home', path: `${basePath}/dashboard` },
                { id: 'trading', label: 'Trading', path: `${basePath}/ttms-trading` },
                { id: 'financial', label: 'Financial', path: `${basePath}/financial` },
                { id: 'billing', label: 'Billing', path: `${basePath}/billing` },
                { id: 'forecasting', label: 'Forecasting', path: `${basePath}/forecasting-management` },
                { id: 'billing-operations', label: 'Billing Ops', path: `${basePath}/billing-operations` },
                { id: 'b2b-operations', label: 'B2B Ops', path: `${basePath}/b2b-operations` },
                { id: 'analytics', label: 'Analytics', path: `${basePath}/analytics` },
                { id: 'settings', label: 'Settings', path: `${basePath}/settings` }
            ]
        },
        crm: {
            title: 'Customers',
            color: '#9c27b0',
            items: [
                { id: 'home', label: 'Home', path: `${basePath}/dashboard` },
                { id: 'customers', label: 'Customers', path: `${basePath}/customers` },
                { id: 'workforce', label: 'Workforce', path: `${basePath}/workforce` },
                { id: 'users', label: 'Users', path: `${basePath}/users` },
                { id: 'support', label: 'Support', path: `${basePath}/support` },
                { id: 'support-management', label: 'Support Ops', path: `${basePath}/support-management` },
                { id: 'team-operations', label: 'Team Ops', path: `${basePath}/team-operations` },
                { id: 'analytics', label: 'Analytics', path: `${basePath}/analytics` },
                { id: 'settings', label: 'Settings', path: `${basePath}/settings` }
            ]
        }
    }), [basePath]);

    const currentMenu = moduleMenus[activeModule];

    const handleItemClick = (path) => {
        navigate(path);
    };

    return (
        <Box sx={{
            width: collapsed ? 72 : 240,
            height: '100vh',
            backgroundColor: '#f6f6f7',
            borderRight: '1px solid #e1e3e5',
            transition: 'width 0.2s ease',
            display: 'flex',
            flexDirection: 'column',
            position: 'fixed',
            left: 0,
            top: 0,
            zIndex: 1200,
            overflow: 'hidden'
        }}>
            <Box sx={{
                p: 2,
                height: 64,
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: 1.5,
                borderBottom: '1px solid #e1e3e5',
                backgroundColor: '#fff'
            }}>
                <Avatar variant="rounded" sx={{
                    bgcolor: currentMenu.color,
                    width: 32,
                    height: 32,
                    fontSize: '0.875rem',
                    fontWeight: 700
                }}>
                    <AdminPanelSettings sx={{ fontSize: 18 }} />
                </Avatar>
                {!collapsed && (
                    <Box>
                        <Typography sx={{ 
                            color: '#202223', 
                            fontWeight: 600, 
                            fontSize: '0.875rem',
                            lineHeight: 1.2
                        }}>
                            {currentMenu.title}
                        </Typography>
                        <Typography sx={{ 
                            color: '#6d7175', 
                            fontSize: '0.75rem' 
                        }}>
                            Aurora WMS
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box sx={{ flex: 1, overflowY: 'auto', py: 1, backgroundColor: '#f6f6f7' }}>
                <List component="nav" disablePadding>
                    {currentMenu.items.map((item) => {
                        const isActive = isActiveLink(item.path);
                        return (
                            <Tooltip 
                                key={item.id} 
                                title={collapsed ? item.label : ''} 
                                placement="right"
                            >
                                <ListItem
                                    button
                                    onClick={() => handleItemClick(item.path)}
                                    sx={{
                                        mx: 1,
                                        my: 0.25,
                                        px: 1.5,
                                        py: 1,
                                        borderRadius: '8px',
                                        minHeight: 36,
                                        justifyContent: collapsed ? 'center' : 'flex-start',
                                        backgroundColor: isActive ? '#fff' : 'transparent',
                                        boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
                                        '&:hover': {
                                            backgroundColor: isActive ? '#fff' : '#edeeef'
                                        },
                                        transition: 'all 0.15s ease'
                                    }}
                                >
                                    <ListItemText
                                        primary={item.label}
                                        sx={{
                                            display: collapsed ? 'none' : 'block',
                                            '& .MuiListItemText-primary': {
                                                color: isActive ? currentMenu.color : '#202223',
                                                fontWeight: isActive ? 600 : 500,
                                                fontSize: '0.875rem'
                                            }
                                        }}
                                    />
                                </ListItem>
                            </Tooltip>
                        );
                    })}
                </List>

                <Divider sx={{ my: 1.5, mx: 2, borderColor: '#e1e3e5' }} />

                <Box sx={{ px: 2, py: 1 }}>
                    <Typography sx={{ 
                        color: '#6d7175', 
                        fontSize: '0.75rem', 
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 1,
                        display: collapsed ? 'none' : 'block'
                    }}>
                        Quick Access
                    </Typography>
                    <List disablePadding>
                        {[
                            { id: 'ai', label: 'AI Assistant', path: `${basePath}/ai` },
                            { id: 'chatbot', label: 'Chatbot', path: `${basePath}/chatbot-management` },
                            { id: 'reports', label: 'Reports', path: `${basePath}/custom-reports` },
                            { id: 'reporting', label: 'Reporting', path: `${basePath}/reporting-dashboard` },
                            { id: 'monitoring', label: 'Monitoring', path: `${basePath}/monitoring-dashboard` },
                            { id: 'live-monitoring', label: 'Live Monitoring', path: `${basePath}/live-monitoring` },
                            { id: 'dashboard-widgets', label: 'Dashboard Widgets', path: `${basePath}/dashboard-widgets` },
                            { id: 'performance', label: 'Performance', path: `${basePath}/performance-optimization` },
                            { id: 'monitoring-operations', label: 'Monitoring Ops', path: `${basePath}/monitoring-operations` },
                            { id: 'analytics-operations', label: 'Analytics Ops', path: `${basePath}/analytics-operations` },
                            { id: 'mlops', label: 'MLOps', path: `${basePath}/mlops` },
                            { id: 'manager-tools', label: 'Manager Tools', path: `${basePath}/manager-tools` }
                        ].map((item) => {
                            const isActive = isActiveLink(item.path);
                            return (
                                <Tooltip 
                                    key={item.id} 
                                    title={collapsed ? item.label : ''} 
                                    placement="right"
                                >
                                    <ListItem
                                        button
                                        onClick={() => handleItemClick(item.path)}
                                        sx={{
                                            px: 1.5,
                                            py: 0.75,
                                            borderRadius: '8px',
                                            minHeight: 32,
                                            justifyContent: collapsed ? 'center' : 'flex-start',
                                            backgroundColor: isActive ? '#fff' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: isActive ? '#fff' : '#edeeef'
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={item.label}
                                            sx={{
                                                display: collapsed ? 'none' : 'block',
                                                '& .MuiListItemText-primary': {
                                                    color: isActive ? '#202223' : '#6d7175',
                                                    fontWeight: isActive ? 600 : 400,
                                                    fontSize: '0.8125rem'
                                                }
                                            }}
                                        />
                                    </ListItem>
                                </Tooltip>
                            );
                        })}
                    </List>
                </Box>
            </Box>

            <Box sx={{ 
                p: 2, 
                borderTop: '1px solid #e1e3e5', 
                backgroundColor: '#fff',
                display: collapsed ? 'none' : 'block' 
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Typography sx={{ color: '#6d7175', fontSize: '0.75rem' }}>
                        System Online
                    </Typography>
                    <Chip 
                        label="v26.1" 
                        size="small" 
                        sx={{ 
                            height: 20, 
                            fontSize: '0.6875rem', 
                            bgcolor: '#f6f6f7', 
                            color: '#6d7175',
                            fontWeight: 500
                        }} 
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default React.memo(SidebarWMS);
